import type { Skill } from './skills';
import type { Skillset } from './skillsets';
import type { Subagent } from './subagents';

import { apiRequest } from './base';

// All of these types need to be kept in sync with server/endpoints/public/search.
// TODO: share these types directly.
export enum ContentType {
  SKILLSET = 'skillset',
  SKILL = 'skill',
  SUBAGENT = 'subagent',
}

export enum SortOption {
  RELEVANCE = 'relevance',
  UPDATED = 'updated',
  ALPHABETICAL = 'alphabetical',
  DOWNLOADS = 'downloads',
}

export type SearchParams = {
  text: string;
  contentType?: Array<ContentType> | null | undefined;
  includePending?: boolean | null | undefined;
  sort?: SortOption | null | undefined;
  // Client-side filters (not sent to server)
  orgCreated?: boolean | null | undefined;
  orgReviewed?: boolean | null | undefined;
  thirdParty?: boolean | null | undefined;
  pendingOnly?: boolean | null | undefined;
};

export type SkillsetWithContentType = Skillset & {
  type: ContentType.SKILLSET;
};

export type SkillWithContentType = Skill & {
  type: ContentType.SKILL;
};

export type SubagentWithContentType = Subagent & {
  type: ContentType.SUBAGENT;
};

export type Content =
  | SkillsetWithContentType
  | SkillWithContentType
  | SubagentWithContentType;

export type SearchResponse = {
  results: Array<Content>;
  hasMore: boolean;
};

/**
 * Searches for any content (skills or skillsets) matching the query
 *
 * @param args - Search arguments
 * @param args.query - Search query with text and optional filters
 * @param args.limit - Maximum number of results per page
 * @param args.offset - Number of results to skip
 *
 * @returns Promise resolving to search response with results and hasMore flag
 */
export const searchContent = async (args: {
  query: SearchParams;
  limit?: number | null;
  offset?: number | null;
}): Promise<SearchResponse> => {
  const { query, limit, offset } = args;
  // Only send server-recognized fields (strip client-side filter params)
  const serverQuery: Record<string, unknown> = {
    text: query.text,
    contentType: query.contentType,
  };
  if (query.includePending) {
    serverQuery.includePending = true;
  }
  const hasSearchText = query.text.trim() !== '';
  const effectiveSort =
    query.sort ??
    (hasSearchText ? SortOption.RELEVANCE : SortOption.DOWNLOADS);
  if (effectiveSort !== SortOption.RELEVANCE) {
    serverQuery.sort = effectiveSort;
  }
  let url = `/search/search?q=${encodeURIComponent(
    JSON.stringify(serverQuery),
  )}`;
  if (limit != null) {
    url += `&limit=${limit}`;
  }
  if (offset != null) {
    url += `&offset=${offset}`;
  }
  return apiRequest<SearchResponse>(url, {
    method: 'GET',
  });
};
